import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useRunLabels } from '../hooks/use-run-labels';
import { useRunStageAttempts } from '../hooks/use-run-stage-attempts';
import type { RunProgress } from '../types/run.api.types';
import { failureCodeLabel } from '../utils/catalog-labels';
import { plainFailureReason } from '../utils/run-stage-reasons';

interface Props {
  runId: string;
  progress: RunProgress;
  /** The state's ONE primary action (e.g. "Re-run batch") — rendered beside "Next". */
  action?: ReactNode;
}

/**
 * The stopped run's ONE status section (design round 2, §1A): title (the stage it stopped at, or
 * the failure code's plain-words title when there is no current stage), a "Why" row with the
 * plain-language reason, and a "Next" row with the failure code's one-line next step plus the
 * single primary action. This is the ONLY place a batch or experiment header shows its failure —
 * it replaces the old phase-strip callout, the experiment "Why it failed" box and the Overview
 * tab's own failure callout, which all showed the same thing up to three times.
 */
export function RunFailureSummary({ runId, progress, action }: Props) {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  const { stageLabel } = useRunLabels();
  const stage = progress.currentStage ?? null;
  // Same cache entry as the expanded row's attempt timeline — tells a rejection from a crash.
  const { data } = useRunStageAttempts(runId, stage ?? '', !!stage);
  const newest = data?.attempts.slice(-1)[0];
  const raw = (progress.failureReason ?? '').trim();
  const code = failureCodeLabel(tCatalog, progress.failureCode);
  // Review fix (1c) — a REJECTED run is an operator's deliberate call, not a crash; say so in
  // both the title and "Why" even before the attempt timeline (which carries `gateOutcome`) has
  // loaded, so the section never reads as a generic "stopped" for a run that was rejected.
  // (There is no `RunProgress.runStatus === 'REJECTED'` on the wire — the failure code is the
  // only signal available before the attempt timeline loads.)
  const rejected =
    progress.failureCode === 'OPERATOR_REJECTED' || newest?.gateOutcome === 'GATE_REJECT';

  const why = (() => {
    if (rejected) {
      return raw
        ? t('failureSummary.rejected', { reason: raw })
        : t('failureSummary.rejectedNoReason');
    }
    if (!raw) return t('failureSummary.noReason');
    const reason = plainFailureReason(raw);
    if (reason.kind === 'gateway') return t('failureSummary.gateway', { code: reason.code });
    if (reason.kind === 'timeout') return t('failureSummary.timeout');
    return reason.text;
  })();

  const title = stage
    ? t(rejected ? 'failureSummary.rejectedAt' : 'failureSummary.stoppedAt', {
        stage: stageLabel(stage),
      })
    : (code?.title ?? t('overview.failedTitle'));
  const next = code && code.next.length > 0 ? code.next : null;

  return (
    <div
      role="alert"
      className="border-error/40 bg-error-50 flex flex-col gap-2 rounded-lg border px-3 py-2.5 text-sm"
    >
      <p className="text-error flex items-center gap-1.5 font-medium">
        <X aria-hidden="true" className="size-4 shrink-0" />
        {title}
      </p>
      <div className="grid grid-cols-[3rem_minmax(0,1fr)] gap-x-2 gap-y-1.5 pl-[1.375rem]">
        <span className="text-muted-foreground pt-0.5 text-xs font-medium tracking-wide uppercase">
          {t('failureSummary.whyLabel')}
        </span>
        <p className="text-foreground min-w-0 break-words" title={raw || undefined}>
          {why}
        </p>
        {next || action ? (
          <>
            <span className="text-muted-foreground pt-0.5 text-xs font-medium tracking-wide uppercase">
              {t('failureSummary.nextLabel')}
            </span>
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <span className="text-foreground min-w-0 break-words">
                {next ?? t('failureSummary.nextDefault')}
              </span>
              {action}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
